/**
 * Fullscreen in-browser camera. Captures a frame from a MediaStream and hands back a File.
 *
 * Extracted from MobilePhotoUpload (the QR-scanned phone page) so the in-survey mobile path can
 * use the same capture UI. Previously the QR page used getUserMedia while the in-survey path used
 * `<input capture="environment">`, which hands off to the OS camera app — a different experience
 * for the same task, and an app-switch that backgrounds the browser mid-survey.
 *
 * Deliberately knows nothing about uploading: it produces a File and the caller decides what to
 * do with it. That is the seam that lets two very different upload pipelines share this.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, SwitchCamera, X } from 'lucide-react';

// play() rejects with AbortError when srcObject is reassigned before the previous play settles —
// which happens when the camera is reopened quickly (capture, close, reopen for the next photo).
// It is a self-resolving race, not a broken camera, so it is retried rather than reported.
const PLAY_RETRY_LIMIT = 3;
const PLAY_RETRY_DELAY_MS = 150;

const IDEAL_WIDTH = 1920;
const IDEAL_HEIGHT = 1080;
const JPEG_QUALITY = 0.9;
const BRAND_TEAL = '#07E5D1';

type FacingMode = 'environment' | 'user';

interface InBrowserCameraProps {
  onCapture: (file: File) => void;
  onClose: () => void;
  /**
   * The camera could not be started. `permanent` distinguishes a camera that will never work here
   * (unsupported, denied, absent, busy) from a transient interruption that a retry usually clears.
   * Only a permanent failure should switch the respondent to the OS camera app — that handoff
   * backgrounds the browser and can discard the in-progress survey.
   */
  onUnavailable: (reason: string, permanent: boolean) => void;
  captureLabel?: string;
}

/** A single frame failed to encode. The camera still works, so we stay in it and let them retry. */
const CAPTURE_FAILED_MESSAGE = 'That shot did not save. Please try again.';

function videoConstraints(facingMode: FacingMode): MediaStreamConstraints {
  return {
    video: { facingMode, width: { ideal: IDEAL_WIDTH }, height: { ideal: IDEAL_HEIGHT } },
    audio: false,
  };
}

/**
 * Reads `.name` off anything error-shaped. `instanceof Error` is NOT safe here: browsers reject
 * getUserMedia and play() with DOMException, which is not an Error subclass in every environment
 * (it is not in jsdom), so an instanceof guard silently misses the very errors we classify on.
 */
function errorName(err: unknown): string {
  return typeof err === 'object' &&
    err !== null &&
    typeof (err as { name?: unknown }).name === 'string'
    ? (err as { name: string }).name
    : '';
}

function errorMessage(err: unknown): string {
  return typeof err === 'object' &&
    err !== null &&
    typeof (err as { message?: unknown }).message === 'string'
    ? (err as { message: string }).message
    : '';
}

/** Failures that a retry will not fix — the camera is unusable on this device. */
const PERMANENT_ERROR_NAMES = new Set([
  'NotAllowedError',
  'NotFoundError',
  'NotReadableError',
  'NotSupportedError',
  'SecurityError',
]);

function isPermanentCameraError(err: unknown): boolean {
  // Default to transient: a spurious retry costs a tap, a spurious permanent fallback costs the
  // survey. Unknown errors are far more likely to be races than hardware faults.
  return PERMANENT_ERROR_NAMES.has(errorName(err));
}

function namedError(name: string, message: string): Error {
  const err = new Error(message);
  err.name = name;
  return err;
}

/** Maps a getUserMedia rejection to something a respondent can act on. */
function describeCameraError(err: unknown): string {
  switch (errorName(err)) {
    case 'NotAllowedError':
      return 'Camera access was denied. Please allow camera permission in your browser settings.';
    case 'NotFoundError':
      return 'No camera found on this device.';
    case 'NotReadableError':
      return 'The camera is in use by another app. Please close it and try again.';
    case 'NotSupportedError':
      return 'This browser does not support in-page camera access.';
    case 'AbortError':
      return 'The camera was interrupted while starting. Please tap to try again.';
    default: {
      const message = errorMessage(err);
      return message
        ? `Camera error: ${message}`
        : 'Could not access the camera. Please try again.';
    }
  }
}

const InBrowserCamera: React.FC<InBrowserCameraProps> = ({
  onCapture,
  onClose,
  onUnavailable,
  captureLabel = 'Take photo',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isMountedRef = useRef(true);

  const [facingMode, setFacingMode] = useState<FacingMode>('environment');
  const [cameraReady, setCameraReady] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const attachStream = useCallback(
    async (mode: FacingMode) => {
      stopStream();
      if (!navigator.mediaDevices?.getUserMedia) {
        // Thrown rather than reported inline so every failure leaves through one async path — a
        // synchronous onUnavailable would set parent state during our own render effect.
        throw namedError('NotSupportedError', 'getUserMedia is unavailable');
      }
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(videoConstraints(mode));
      } catch (err) {
        // Some devices reject the ideal resolution outright; a bare request usually succeeds.
        if (errorName(err) === 'OverconstrainedError') {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        } else {
          throw err;
        }
      }

      // The await above can resolve after the respondent closed the camera or advanced. Releasing
      // the stream here is what stops the camera light staying on after unmount.
      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      if (!videoRef.current) throw new Error('Video element not available');
      videoRef.current.srcObject = stream;

      for (let attempt = 0; attempt < PLAY_RETRY_LIMIT; attempt++) {
        try {
          await videoRef.current.play();
          break;
        } catch (err) {
          const interrupted = errorName(err) === 'AbortError';
          // Anything else (or a race that will not settle) is a genuine failure worth reporting.
          if (!interrupted || attempt === PLAY_RETRY_LIMIT - 1) throw err;
          if (!isMountedRef.current) return;
          await new Promise((resolve) => setTimeout(resolve, PLAY_RETRY_DELAY_MS));
          if (!isMountedRef.current || !videoRef.current) return;
        }
      }

      if (isMountedRef.current) setCameraReady(true);
    },
    [stopStream],
  );

  // Start on mount, and restart when the respondent flips front/back. cameraReady is cleared by
  // the switch handler rather than here, so this effect never sets state synchronously.
  useEffect(() => {
    isMountedRef.current = true;

    attachStream(facingMode).catch((err) => {
      if (!isMountedRef.current) return;
      onUnavailable(describeCameraError(err), isPermanentCameraError(err));
    });

    return () => {
      isMountedRef.current = false;
      stopStream();
    };
  }, [facingMode, attachStream, stopStream, onUnavailable]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !cameraReady) return;

    setCaptureError(null);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // No 2D context at all is a device limitation, not a bad frame — fall back for real.
      onUnavailable('This device cannot capture photos in the browser.', true);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          // Encoding one frame failed. Previously this went through onUnavailable, which
          // permanently latched the in-page camera off for the question AND told the respondent
          // to "try again" — advice they could not act on. Keep the camera open instead.
          setCaptureError(CAPTURE_FAILED_MESSAGE);
          return;
        }
        // Stop the stream before handing the file over: the caller may unmount us immediately,
        // and an orphaned stream leaves the camera indicator on.
        stopStream();
        onCapture(new File([blob], `capture_${blob.size}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  }, [cameraReady, onCapture, onUnavailable, stopStream]);

  return (
    <div
      className="fixed inset-0 bg-black z-50 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Camera"
    >
      <canvas ref={canvasRef} className="hidden" />

      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          // Mirror the selfie view so it behaves like a mirror, as every camera app does.
          style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
        />
        {captureError && cameraReady && (
          <div
            className="absolute bottom-4 left-4 right-4 bg-red-600/95 text-white text-sm
                          rounded-lg px-4 py-3 text-center"
            role="alert"
          >
            {captureError}
          </div>
        )}
        {!cameraReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center text-white">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
              <p>Starting camera…</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-black/90 p-6 pb-10 flex items-center justify-center gap-8">
        <button
          type="button"
          onClick={() => {
            stopStream();
            onClose();
          }}
          aria-label="Cancel"
          className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center"
        >
          <X className="w-7 h-7 text-white" />
        </button>

        <button
          type="button"
          onClick={capture}
          disabled={!cameraReady}
          aria-label={captureLabel}
          className={`w-20 h-20 rounded-full border-4 border-white flex items-center justify-center
                     ${cameraReady ? 'bg-white' : 'bg-white/50'}`}
        >
          <div
            className="w-16 h-16 rounded-full"
            style={{ backgroundColor: cameraReady ? BRAND_TEAL : '#9CA3AF' }}
          />
        </button>

        <button
          type="button"
          onClick={() => {
            setCameraReady(false);
            setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'));
          }}
          disabled={!cameraReady}
          aria-label="Switch camera"
          className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center"
        >
          <SwitchCamera className="w-7 h-7 text-white" />
        </button>
      </div>
    </div>
  );
};

export default InBrowserCamera;
